import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useFieldArray, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { IssueInvoiceSchema, type IssueInvoiceInput } from '@app/shared';
import { useInvoices, useIssueInvoice } from '../features/invoices/api';
import { getApiErrorMessage } from '../lib/api-client';

export function InvoicesPage(): JSX.Element {
  const [page, setPage] = useState(1);
  const { data, isLoading, isError, error } = useInvoices({ page, pageSize: 25 });
  const [showForm, setShowForm] = useState(false);

  return (
    <section className="grid gap-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">電子發票</h1>
        <button
          type="button"
          onClick={() => setShowForm((v) => !v)}
          className="bg-blue-600 text-white px-3 py-1.5 rounded hover:bg-blue-700"
        >
          {showForm ? '收起' : '開立發票'}
        </button>
      </div>

      {showForm ? <IssueInvoiceForm onIssued={() => setShowForm(false)} /> : null}

      {isLoading ? (
        <div role="status" className="text-slate-500">載入中...</div>
      ) : isError ? (
        <div role="alert" className="text-red-600">
          載入失敗：{error instanceof Error ? error.message : '未知錯誤'}
        </div>
      ) : (
        <>
          <table className="w-full text-sm border bg-white">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left px-2 py-1.5">發票號碼</th>
                <th className="text-left px-2 py-1.5">類別</th>
                <th className="text-left px-2 py-1.5">日期</th>
                <th className="text-left px-2 py-1.5">買方</th>
                <th className="text-right px-2 py-1.5">含稅總額</th>
                <th className="text-center px-2 py-1.5">狀態</th>
                <th className="text-center px-2 py-1.5">上傳</th>
              </tr>
            </thead>
            <tbody>
              {(data?.data ?? []).length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-2 py-4 text-center text-slate-400">
                    尚無發票
                  </td>
                </tr>
              ) : (
                (data?.data ?? []).map((inv) => (
                  <tr key={inv.id} className="border-t">
                    <td className="px-2 py-1.5 font-mono">
                      <Link
                        to={`/invoices/${inv.id}/certificate`}
                        className="text-blue-600 hover:underline"
                        title="檢視證明聯(可列印 / 存 PDF)"
                      >
                        {inv.invoiceNo}
                      </Link>
                    </td>
                    <td className="px-2 py-1.5">{inv.type}</td>
                    <td className="px-2 py-1.5">{inv.invoiceDate}</td>
                    <td className="px-2 py-1.5">
                      {inv.buyerName || inv.buyerTaxId || <span className="text-slate-400">—</span>}
                    </td>
                    <td className="px-2 py-1.5 text-right font-mono">{inv.totalAmount}</td>
                    <td className="px-2 py-1.5 text-center">{statusLabel(inv.status)}</td>
                    <td className="px-2 py-1.5 text-center">{uploadLabel(inv.uploadStatus)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          <Pager
            page={page}
            pageSize={data?.meta.pageSize ?? 25}
            total={data?.meta.total ?? 0}
            onPage={setPage}
          />
        </>
      )}
    </section>
  );
}

function statusLabel(s: string): JSX.Element {
  const map: Record<string, string> = { ISSUED: '已開立', VOIDED: '已作廢', ALLOWANCE: '已折讓' };
  return <span>{map[s] ?? s}</span>;
}

function uploadLabel(s: string): JSX.Element {
  const map: Record<string, { t: string; c: string }> = {
    PENDING: { t: '待上傳', c: 'text-amber-600' },
    // REQ-20260915003:上傳財政部由 ezPay 負責,系統端只知道「已交 ezPay 開立」
    UPLOADED: { t: '已交 ezPay', c: 'text-blue-600' },
    CONFIRMED: { t: '已確認', c: 'text-green-700' },
    FAILED: { t: '失敗', c: 'text-red-600' },
  };
  const v = map[s] ?? { t: s, c: '' };
  return <span className={v.c}>{v.t}</span>;
}

function Pager({
  page,
  pageSize,
  total,
  onPage,
}: {
  page: number;
  pageSize: number;
  total: number;
  onPage: (p: number) => void;
}): JSX.Element {
  const maxPage = Math.max(1, Math.ceil(total / pageSize));
  return (
    <div className="flex items-center gap-2 text-sm text-slate-600">
      <button
        type="button"
        disabled={page <= 1}
        onClick={() => onPage(page - 1)}
        className="border rounded px-2 py-1 disabled:opacity-40"
      >
        上一頁
      </button>
      <span>
        {page} / {maxPage}（共 {total} 筆）
      </span>
      <button
        type="button"
        disabled={page >= maxPage}
        onClick={() => onPage(page + 1)}
        className="border rounded px-2 py-1 disabled:opacity-40"
      >
        下一頁
      </button>
    </div>
  );
}

// 零稅率需 ezPay 的「報關標記」,本系統未提供,不列入選項(REQ-20260915003)
const TAX_TYPES = [
  { v: 'TAXABLE', t: '應稅' },
  { v: 'EXEMPT', t: '免稅' },
] as const;

function IssueInvoiceForm({ onIssued }: { onIssued: () => void }): JSX.Element {
  const issueMutation = useIssueInvoice();
  const [flash, setFlash] = useState<string | null>(null);

  const {
    register,
    control,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm<IssueInvoiceInput>({
    resolver: zodResolver(IssueInvoiceSchema),
    defaultValues: {
      type: 'B2C',
      buyerTaxId: '',
      buyerName: '',
      carrierType: '',
      carrierId: '',
      donationCode: '',
      taxRate: 0.05,
      lines: [{ description: '', quantity: 1, unit: '', unitPrice: 0, taxType: 'TAXABLE' }],
    },
  });

  const { fields, append, remove } = useFieldArray({ control, name: 'lines' });
  const type = watch('type');
  const lines = watch('lines');
  // ezPay 規定 B2B 品項為未稅、B2C 為含稅;小計與後端同樣逐項取整數
  const previewSum = (lines ?? []).reduce(
    (s, l) => s + Math.round((Number(l.quantity) || 0) * (Number(l.unitPrice) || 0)),
    0,
  );

  const onSubmit = (values: IssueInvoiceInput): void => {
    setFlash(null);
    issueMutation.mutate(values, {
      onSuccess: (inv) => {
        setFlash(`已開立 ${inv.invoiceNo}（含稅 ${inv.totalAmount}）`);
        reset();
        onIssued();
      },
    });
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="grid gap-3 bg-white border rounded p-4"
    >
      <h2 className="text-base font-semibold">開立發票</h2>

      <div className="flex flex-wrap gap-4">
        <label className="grid gap-1 text-sm">
          <span className="text-slate-700">類別</span>
          <select {...register('type')} className={`${inputCls} w-40`}>
            <option value="B2C">B2C（二聯/載具）</option>
            <option value="B2B">B2B（三聯/統編）</option>
          </select>
        </label>
        <label className="grid gap-1 text-sm">
          <span className="text-slate-700">稅率</span>
          <input
            type="number"
            step="0.0001"
            {...register('taxRate', { valueAsNumber: true })}
            className={`${inputCls} w-28`}
          />
        </label>
      </div>

      {type === 'B2B' ? (
        <div className="flex flex-wrap gap-4">
          <Field label="買方統一編號" error={errors.buyerTaxId?.message}>
            <input {...register('buyerTaxId')} className={`${inputCls} w-40`} placeholder="12345678" />
          </Field>
          <Field label="買方名稱" error={errors.buyerName?.message}>
            <input {...register('buyerName')} className={`${inputCls} w-64`} />
          </Field>
        </div>
      ) : (
        <div className="flex flex-wrap gap-4">
          <Field label="買方名稱" error={errors.buyerName?.message}>
            <input {...register('buyerName')} className={`${inputCls} w-40`} placeholder="個人姓名" />
          </Field>
          <Field label="載具類別" error={errors.carrierType?.message}>
            <select {...register('carrierType')} className={`${inputCls} w-32`}>
              <option value="">無</option>
              <option value="0">手機條碼</option>
              <option value="1">自然人憑證</option>
            </select>
          </Field>
          <Field label="載具號碼" error={errors.carrierId?.message}>
            <input {...register('carrierId')} className={`${inputCls} w-48`} placeholder="/ABC+123" />
          </Field>
          <Field label="捐贈愛心碼（與載具擇一）" error={errors.donationCode?.message}>
            <input {...register('donationCode')} className={`${inputCls} w-32`} />
          </Field>
        </div>
      )}

      <div className="grid gap-2">
        <span className="text-sm font-semibold text-slate-700">品項</span>
        {fields.map((f, i) => (
          <div key={f.id} className="flex flex-wrap items-end gap-2">
            <Field label="品名" error={errors.lines?.[i]?.description?.message}>
              <input {...register(`lines.${i}.description` as const)} className={`${inputCls} w-56`} />
            </Field>
            <Field label="數量" error={errors.lines?.[i]?.quantity?.message}>
              <input
                type="number"
                step="any"
                {...register(`lines.${i}.quantity` as const, { valueAsNumber: true })}
                className={`${inputCls} w-24`}
              />
            </Field>
            <Field label="單位" error={errors.lines?.[i]?.unit?.message}>
              <input {...register(`lines.${i}.unit` as const)} className={`${inputCls} w-20`} />
            </Field>
            <Field
              label={type === 'B2B' ? '單價（未稅）' : '單價（含稅）'}
              error={errors.lines?.[i]?.unitPrice?.message}
            >
              <input
                type="number"
                step="any"
                {...register(`lines.${i}.unitPrice` as const, { valueAsNumber: true })}
                className={`${inputCls} w-28`}
              />
            </Field>
            <Field label="稅別" error={undefined}>
              <select {...register(`lines.${i}.taxType` as const)} className={`${inputCls} w-24`}>
                {TAX_TYPES.map((t) => (
                  <option key={t.v} value={t.v}>
                    {t.t}
                  </option>
                ))}
              </select>
            </Field>
            <button
              type="button"
              onClick={() => remove(i)}
              disabled={fields.length <= 1}
              className="text-red-600 text-sm px-2 py-1.5 disabled:opacity-30"
            >
              刪除
            </button>
          </div>
        ))}
        <div>
          <button
            type="button"
            onClick={() => append({ description: '', quantity: 1, unit: '', unitPrice: 0, taxType: 'TAXABLE' })}
            className="border border-blue-600 text-blue-600 px-2 py-1 rounded text-sm hover:bg-blue-50"
          >
            ＋ 新增品項
          </button>
        </div>
      </div>

      <div className="text-sm text-slate-600">
        {type === 'B2B' ? '未稅小計' : '含稅合計'}（預覽）：<span className="font-mono">{previewSum}</span>
      </div>

      {issueMutation.isError ? (
        <div role="alert" className="text-red-600 text-sm">
          開立失敗：{getApiErrorMessage(issueMutation.error, '未知錯誤')}
        </div>
      ) : null}
      {flash ? (
        <div role="status" className="text-green-700 text-sm">
          {flash}
        </div>
      ) : null}

      <div>
        <button
          type="submit"
          disabled={issueMutation.isPending}
          className="bg-blue-600 text-white px-3 py-1.5 rounded hover:bg-blue-700 disabled:opacity-50"
        >
          {issueMutation.isPending ? '開立中…' : '確認開立'}
        </button>
      </div>
    </form>
  );
}

function Field({
  label,
  error,
  children,
}: {
  label: string;
  error: string | undefined;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <label className="grid gap-1 text-sm">
      <span className="text-slate-700">{label}</span>
      {children}
      {error ? (
        <span role="alert" className="text-red-600 text-xs">
          {error}
        </span>
      ) : null}
    </label>
  );
}

const inputCls =
  'border rounded px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-400';
